// @ai-generated 2026-06-22T12:01:20+00:00 by malte.ulfers@tngtech.com using claude-sonnet-4-6

import type {
    ApiAttachment,
    ApiAttachmentDownloadResponse,
    ApiAttachmentEntityOptions,
    ApiAttachmentUploadRequest,
    ApiAttachmentUploadResponse,
} from '@Model';
import { EnumAttachmentCategory } from '@Model';
import {
    EnumFilterExpressionOperation,
    isOk,
    type ListInfo,
    type ListRequest,
    type Result,
    useToasts,
    useSynqoHttp,
} from '@synqo/frontend-component-library';
import { useFileDownload } from '@Hooks/useFileDownload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useUploadToAzureBlobStorage } from './useUploadToAzureBlobStorage';

export function useEntityAttachments(baseUrl: string, entityPath: string, entityId: string | undefined) {
    const uploadToBlobStorage = useUploadToAzureBlobStorage();
    const http = useSynqoHttp(`${baseUrl}/${entityPath}/${entityId ?? ''}/attachments`);
    const { triggerUrlDownload } = useFileDownload();
    const queryClient = useQueryClient();
    const { pushToast, pushAllToasts } = useToasts();

    const notifyError = useCallback((message: string) => pushToast({ message, variant: 'error' }), [pushToast]);

    const listKey = useMemo(() => ['attachments', entityPath, entityId] as const, [entityPath, entityId]);
    const optionsKey = useMemo(() => ['attachments', entityPath, 'options'] as const, [entityPath]);

    const optionsQuery = useQuery({
        queryKey: optionsKey,
        queryFn: () => http.get<ApiAttachmentEntityOptions>('/options'),
        enabled: !!entityId,
    });

    const emptyListRequest = useMemo<ListRequest>(
        () => ({
            page_no: 0,
            page_size: 100,
            full_text_search: '',
            sort_columns: [],
            filter_columns: { operation: EnumFilterExpressionOperation.And, filters: [] },
        }),
        [],
    );

    const listQuery = useQuery({
        queryKey: listKey,
        queryFn: () => http.post<Result<ListInfo<ApiAttachment>>, ListRequest>(':query', emptyListRequest),
        enabled: !!entityId,
    });

    const invalidateList = useCallback(() => {
        void queryClient.invalidateQueries({ queryKey: listKey });
    }, [queryClient, listKey]);

    // @ai-generated 2026-06-22T12:01:20+00:00 by malte.ulfers@tngtech.com using claude-sonnet-4-6
    // Cleanup-only: failure here is not surfaced to the user — the upload-failure notification has
    // already been shown by the caller, and the orphan row will be reclaimed by the cleanup cron.
    const cancelUpload = useCallback(
        async (attachmentId: string) => {
            try {
                await http.post<Result<boolean>, undefined>(`/${attachmentId}:cancel_upload`, undefined);
            } catch {
                // Swallow — see comment above.
            }
        },
        [http],
    );

    // Percent of this client's in-flight uploads keyed by attachment id; drives the per-row progress ring.
    const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});

    const setUploadPercent = useCallback((attachmentId: string, percent: number) => {
        setUploadProgress((prev) => ({ ...prev, [attachmentId]: percent }));
    }, []);

    const removeUploadProgress = useCallback((attachmentId: string) => {
        setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[attachmentId];
            return next;
        });
    }, []);

    // @ai-generated 2026-07-20T11:01:17+00:00 by philipp.kammerer@synqony.com using claude-fable-5
    const uploadMutation = useMutation({
        mutationFn: async (file: File) => {
            const request: ApiAttachmentUploadRequest = {
                file_name: file.name,
                mime_type: file.type,
                file_size: file.size,
                category: EnumAttachmentCategory.None,
                key: '',
            };

            const initiate = await http.post<Result<ApiAttachmentUploadResponse>, ApiAttachmentUploadRequest>(
                '',
                request,
            );
            if (!initiate || !isOk(initiate)) {
                if (initiate) {
                    pushAllToasts(initiate.messages);
                } else {
                    notifyError('Anhang konnte nicht hochgeladen werden.');
                }
                throw new Error('Upload initiation failed');
            }

            const { attachment_id, upload_url } = initiate.data;

            await listQuery.refetch();

            setUploadPercent(attachment_id, 0);
            try {
                const upload = await uploadToBlobStorage({
                    uploadUrl: upload_url,
                    file,
                    onProgress: (percent) => setUploadPercent(attachment_id, percent),
                });
                if (!isOk(upload)) {
                    pushAllToasts(upload.messages);
                    await cancelUpload(attachment_id);
                    invalidateList();
                    throw new Error('Blob upload failed');
                }
            } finally {
                removeUploadProgress(attachment_id);
            }
        },
        onSuccess: invalidateList,
    });

    const downloadMutation = useMutation({
        mutationFn: async (attachmentId: string) => {
            const response = await http.get<Result<ApiAttachmentDownloadResponse>>(`/${attachmentId}:download`);
            if (!response || !isOk(response)) {
                if (response) {
                    pushAllToasts(response.messages);
                } else {
                    notifyError('Download-Link konnte nicht geladen werden.');
                }
                throw new Error('Could not retrieve download URL');
            }
            triggerUrlDownload(response.data.download_url);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (attachmentId: string) => {
            const response = await http.delete<Result<boolean>>(`/${attachmentId}`);
            if (!response || !isOk(response)) {
                if (response) {
                    pushAllToasts(response.messages);
                } else {
                    notifyError('Anhang konnte nicht gelöscht werden.');
                }
                throw new Error('Delete failed');
            }
        },
        onSuccess: invalidateList,
    });

    const listData = listQuery.data;
    const attachments = listData && isOk(listData) ? listData.data.items : [];

    return {
        attachments,
        options: optionsQuery.data,
        isLoading: listQuery.isLoading || optionsQuery.isLoading,
        upload: uploadMutation.mutateAsync,
        uploadProgress,
        download: downloadMutation.mutateAsync,
        delete: deleteMutation.mutateAsync,
        invalidateList,
    };
}
